import {FunctionComponent} from 'react';
import {FormikProps} from 'formik';
import {useTranslation} from 'react-i18next';
import {AdvisorProfileFormValues} from '@/components/advisor-profile-form/interfaces/form-values';
import FieldWrapper from '@/components/form/field-wrapper';
import FormControl from '@/components/form/form-control';
import Input from '@/components/form/input';
import SectionTitle from '@/components/section-title';
import {FieldMetadata} from '@/interfaces/form';

interface AdvisorGeneralAgentSectionProps {
  fieldsMetadata: {
    cons8_generalAgentName: FieldMetadata;
    cons10_generalAgentEmail: FieldMetadata;
    cons11_generalAgentEmail_confirmation: FieldMetadata;
  };
  formik: Pick<FormikProps<Partial<AdvisorProfileFormValues>>, 'handleChange' | 'handleBlur' | 'values' | 'errors'>;
}

export const AdvisorGeneralAgentSection: FunctionComponent<AdvisorGeneralAgentSectionProps> = ({
  fieldsMetadata,
  formik: {handleChange, handleBlur, values, errors},
}) => {
  const {t} = useTranslation('common');

  return (
    <>
      <SectionTitle>{t('components.advisor-profile-form.general-agent-info')}</SectionTitle>

      <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-3">
        <FormControl
          isRequired={fieldsMetadata.cons8_generalAgentName.isRequired}
          isInError={!!errors.cons8_generalAgentName}
          errors={[errors.cons8_generalAgentName ?? '']}
        >
          <FieldWrapper
            label={t('components.advisor-profile-form.label-general-agent-fullname')}
            labelMultiline={false}
            identifier="CONS8"
            hidden={fieldsMetadata.cons8_generalAgentName.isHidden}
          >
            <Input
              data-testid="CONS8-input"
              id="CONS8"
              name="CONS8"
              onBlur={handleBlur('cons8_generalAgentName')}
              onChange={handleChange('cons8_generalAgentName')}
              readOnly={fieldsMetadata.cons8_generalAgentName.isReadOnly}
              value={values.cons8_generalAgentName ?? ''}
            />
          </FieldWrapper>
        </FormControl>

        <FormControl
          isRequired={fieldsMetadata.cons10_generalAgentEmail.isRequired}
          isInError={!!errors.cons10_generalAgentEmail}
          errors={[errors.cons10_generalAgentEmail ?? '']}
        >
          <FieldWrapper
            label={t('components.advisor-profile-form.label-email-for-followups')}
            labelMultiline={false}
            identifier="CONS10"
            hidden={fieldsMetadata.cons10_generalAgentEmail.isHidden}
            helpDialogTitle={t('components.advisor-profile-form.label-email-for-followups-helper-title')}
            helpDialogContent={t('components.advisor-profile-form.label-email-for-followups-helper-content')}
            helpDialogToggleAccessibilityLabel={t('components.field-wrapper.help-button-label-accessibility')}
          >
            <Input
              data-testid="CONS10-input"
              id="CONS10"
              name="CONS10"
              onBlur={handleBlur('cons10_generalAgentEmail')}
              onChange={handleChange('cons10_generalAgentEmail')}
              readOnly={fieldsMetadata.cons10_generalAgentEmail.isReadOnly}
              value={values.cons10_generalAgentEmail ?? ''}
            />
          </FieldWrapper>
        </FormControl>

        <FormControl
          isRequired={fieldsMetadata.cons11_generalAgentEmail_confirmation.isRequired}
          isInError={!!errors.cons11_generalAgentEmail_confirmation}
          errors={[errors.cons11_generalAgentEmail_confirmation ?? '']}
        >
          <FieldWrapper
            label={t('components.advisor-profile-form.label-email-for-followups-confirmation')}
            labelMultiline={false}
            identifier="CONS11"
            hidden={fieldsMetadata.cons11_generalAgentEmail_confirmation.isHidden}
          >
            <Input
              data-testid="CONS11-input"
              id="CONS11"
              name="CONS11"
              onBlur={handleBlur('cons11_generalAgentEmail_confirmation')}
              onChange={handleChange('cons11_generalAgentEmail_confirmation')}
              readOnly={fieldsMetadata.cons11_generalAgentEmail_confirmation.isReadOnly}
              value={values.cons11_generalAgentEmail_confirmation ?? ''}
            />
          </FieldWrapper>
        </FormControl>
      </div>
    </>
  );
};
